/**
 * The filter bar. A13.
 *
 * Everything here already works without it: the chips are links, the sheets
 * are <details>, and the URL is the state. This element removes the page
 * reload and nothing else — if it fails to load, filtering still filters.
 *
 * interactions.md §3.8: the grid fades out, reflows, and fades back in. The
 * count updates last, because updating it first tells her the answer before
 * she can see it.
 */
class FilterBar extends HTMLElement {
  connectedCallback() {
    this.scrim = this.querySelector('[data-scrim]');
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('click', this.onClick);
    this.addEventListener('toggle', this.onToggle, true);
    this.addEventListener('keydown', this.onKeydown);
    this.scrim?.addEventListener('click', () => this.closeAll());

    // The back button has to undo a filter, not leave the page.
    this.onPop = () => this.render(location.href, { push: false });
    window.addEventListener('popstate', this.onPop);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
  }

  get details() {
    return [...this.querySelectorAll('[data-filter]')];
  }

  onKeydown = (event) => {
    if (event.key === 'Escape') this.closeAll();
  };

  /** One sheet at a time. Two open sheets is two answers to one question. */
  onToggle = (event) => {
    const panel = event.target;
    if (!panel.matches('[data-filter]')) return;

    if (panel.open) {
      this.details.forEach((other) => {
        if (other !== panel) other.open = false;
      });
    }
    if (this.scrim) this.scrim.hidden = !this.details.some((d) => d.open);
  };

  onClick = (event) => {
    if (event.target.closest('[data-close]')) {
      event.preventDefault();
      this.closeAll();
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link || link.getAttribute('aria-disabled') === 'true') return;
    if (!this.contains(link)) return;

    event.preventDefault();
    this.render(link.href);
  };

  closeAll() {
    this.details.forEach((d) => (d.open = false));
    if (this.scrim) this.scrim.hidden = true;
  }

  async render(url, { push = true } = {}) {
    const grid = document.getElementById('product-grid');
    const results = document.getElementById('collection-results');
    if (!results) {
      location.assign(url);
      return;
    }

    grid?.setAttribute('data-loading', '');

    try {
      const target = new URL(url, location.origin);
      if (this.sectionId) target.searchParams.set('section_id', this.sectionId);

      const markup = await fetch(target, { headers: { Accept: 'text/html' } }).then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      });

      const next = new DOMParser().parseFromString(markup, 'text/html');
      const nextResults = next.getElementById('collection-results');
      const nextBar = next.querySelector('[data-filter-bar]');
      if (!nextResults || !nextBar) throw new Error('unexpected section markup');

      results.replaceWith(nextResults);
      this.replaceChildren(...nextBar.childNodes);
      this.scrim = this.querySelector('[data-scrim]');

      if (push) {
        const clean = new URL(url, location.origin);
        clean.searchParams.delete('section_id');
        history.pushState({}, '', clean);
      }
    } catch {
      // Whatever went wrong, the link still works. Let the browser do it.
      location.assign(url);
    }
  }
}

customElements.define('filter-bar', FilterBar);
